import axios from 'axios'
import { toast } from 'react-hot-toast'

// Determine API URL based on environment
const getApiBaseUrl = () => {
  // If VITE_API_URL is set and not empty, use it
  if (import.meta.env.VITE_API_URL && import.meta.env.VITE_API_URL.trim() !== '') {
    return import.meta.env.VITE_API_URL
  }
  
  // Use relative path (empty string) to let nginx proxy handle routing
  // This avoids mixed content issues (HTTPS page calling HTTP API)
  // Nginx will route /api/* to the backend automatically
  return ''
}

const API_BASE_URL = getApiBaseUrl()

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// --- Auth: attach JWT to every request ---
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('tradescope_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Intercepteur pour gérer les erreurs globalement
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Gestion des erreurs réseau
    if (!error.response) {
      toast.error('Erreur de connexion au serveur')
      return Promise.reject({
        message: 'Impossible de se connecter au serveur. Vérifiez votre connexion internet.',
        code: 'NETWORK_ERROR'
      })
    }

    // Gestion des erreurs HTTP
    const { status, data } = error.response
    let errorMessage = 'Une erreur inattendue s\'est produite'

    // 401: token absent/expired -> force re-login (skip for the login endpoint itself)
    const isLoginCall = error.config?.url?.includes('/api/auth/login')
    if (status === 401 && !isLoginCall) {
      localStorage.removeItem('tradescope_token')
      localStorage.removeItem('tradescope_token_expiry')
      // Redirect to login page (preserve current path for post-login return)
      if (window.location.pathname !== '/login') {
        toast.error('Session expirée - veuillez vous reconnecter')
        window.location.href = '/login'
      }
      return Promise.reject({ message: 'Session expirée', status, originalError: error })
    }

    switch (status) {
      case 400:
        errorMessage = data?.detail || 'Requête invalide'
        break
      case 401:
        errorMessage = data?.detail || 'Code PIN incorrect'
        break
      case 403:
        errorMessage = 'Accès interdit'
        break
      case 404:
        errorMessage = 'Ressource non trouvée'
        break
      case 422:
        errorMessage = data?.detail || 'Données invalides'
        break
      case 500:
        errorMessage = 'Erreur interne du serveur'
        break
      case 503:
        errorMessage = 'Service temporairement indisponible'
        break
      default:
        errorMessage = `Erreur ${status}: ${data?.detail || error.message}`
    }

    return Promise.reject({
      message: errorMessage,
      status,
      originalError: error
    })
  }
)


// Service API avec cache et gestion d'erreurs améliorée
class ApiService {
  constructor() {
    this.cache = new Map()
    this.cacheTimeout = 5 * 60 * 1000 // 5 minutes
    this.retryAttempts = 3
    this.retryDelay = 1000 // 1 seconde
    this.loadingPromises = new Map() // Pour éviter les appels API dupliqués
    this.persistentCacheKey = 'tradescope_filter_cache'
    this.loadPersistentCache()
  }

  // Charger le cache persistant depuis localStorage
  loadPersistentCache() {
    try {
      const stored = localStorage.getItem(this.persistentCacheKey)
      if (stored) {
        const parsedCache = JSON.parse(stored)
        Object.entries(parsedCache).forEach(([key, item]) => {
          // Vérifier si l'item n'est pas expiré
          if (Date.now() < item.expiry) {
            this.cache.set(key, item)
          }
        })
      }
    } catch (error) {
      console.warn('Erreur lors du chargement du cache persistant:', error)
    }
  }

  // Sauvegarder le cache dans localStorage
  savePersistentCache() {
    try {
      const cacheObject = {}
      this.cache.forEach((value, key) => {
        // Ne sauvegarder que les caches de filtres (pas les requêtes de données)
        if (key.startsWith('filter_')) {
          cacheObject[key] = value
        }
      })
      localStorage.setItem(this.persistentCacheKey, JSON.stringify(cacheObject))
    } catch (error) {
      console.warn('Erreur lors de la sauvegarde du cache persistant:', error)
    }
  }

  // Méthode pour retry automatique
  async withRetry(fn, attempts = this.retryAttempts) {
    try {
      return await fn()
    } catch (error) {
      if (attempts > 1 && this.shouldRetry(error)) {
        await this.delay(this.retryDelay)
        return this.withRetry(fn, attempts - 1)
      }
      throw error
    }
  }

  shouldRetry(error) {
    // Retry pour les erreurs réseau et 5xx
    return !error.response || (error.response.status >= 500)
  }

  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  isValidCache(key) {
    const item = this.cache.get(key)
    if (!item) return false
    
    if (Date.now() > item.expiry) {
      this.cache.delete(key)
      return false
    }
    
    return true
  }

  setCache(key, data) {
    this.cache.set(key, {
      data,
      expiry: Date.now() + this.cacheTimeout
    })
    
    // Sauvegarder dans localStorage pour les filtres
    if (key.startsWith('filter_')) {
      this.savePersistentCache()
    }
  }

  getCache(key) {
    const item = this.cache.get(key)
    if (!item) return null
    
    return item.data
  }
}

// Instance du service API
const apiService = new ApiService()

// Services API
export const apiServiceMethods = {
  // Requête principale avec filtres (legacy — still works but prefer queryAnalytics/queryTable)
  async queryData(filters, options = {}) {
    const cacheKey = `query_${JSON.stringify({ filters, options })}`
    
    // Vérifier le cache
    if (apiService.isValidCache(cacheKey)) {
      return apiService.getCache(cacheKey)
    }

    return apiService.withRetry(async () => {
      const payload = {
        filters,
        include_kpi: options.include_kpi !== false,
        include_timeseries: options.include_timeseries !== false,
        include_top: options.include_top !== false,
        include_table: options.include_table !== false,
        timeseries_period: options.timeseries_period || 'monthly',
        top_limit: options.top_limit || 10,
        ranking_by: options.ranking_by || 'value',
        table_page: options.table_page || 1,
        table_size: options.table_size || 50,
        table_sort: options.table_sort || null,
        table_direction: options.table_direction || 'asc'
      }
      
      const response = await api.post('/api/query', payload)
      
      if (!response.data) {
        throw new Error('Aucune donnée reçue du serveur')
      }
      
      apiService.setCache(cacheKey, response.data)
      return response.data
    })
  },

  // Analytics-only fetch (KPI + timeseries + top charts) — cached independently of table pagination
  async queryAnalytics(filters, rankingBy = 'value') {
    const cacheKey = `analytics_${JSON.stringify(filters)}_${rankingBy}`

    if (apiService.isValidCache(cacheKey)) {
      return apiService.getCache(cacheKey)
    }

    // Deduplicate concurrent calls
    if (apiService.loadingPromises.has(cacheKey)) {
      return apiService.loadingPromises.get(cacheKey)
    }

    const promise = apiService.withRetry(async () => {
      try {
        const response = await api.post('/api/query', {
          filters,
          include_kpi: true,
          include_timeseries: true,
          include_top: true,
          include_table: false,
          ranking_by: rankingBy,
          timeseries_period: 'monthly',
          top_limit: 10,
        })
        if (!response.data) throw new Error('Aucune donnée reçue du serveur')
        apiService.setCache(cacheKey, response.data)
        return response.data
      } finally {
        apiService.loadingPromises.delete(cacheKey)
      }
    })
    apiService.loadingPromises.set(cacheKey, promise)
    return promise
  },

  // Table-only fetch (paginated data) — cached per page/sort, independent of analytics
  async queryTable(filters, page = 1, size = 50, sortField = null, sortDirection = 'asc') {
    const cacheKey = `table_${JSON.stringify(filters)}_${page}_${size}_${sortField}_${sortDirection}`

    if (apiService.isValidCache(cacheKey)) {
      return apiService.getCache(cacheKey)
    }

    if (apiService.loadingPromises.has(cacheKey)) {
      return apiService.loadingPromises.get(cacheKey)
    }

    const promise = apiService.withRetry(async () => {
      try {
        const response = await api.post('/api/query', {
          filters,
          include_kpi: false,
          include_timeseries: false,
          include_top: false,
          include_table: true,
          table_page: page,
          table_size: size,
          table_sort: sortField,
          table_direction: sortDirection,
        })
        if (!response.data) throw new Error('Aucune donnée reçue du serveur')
        // Cache table results with shorter TTL (2 min) since they're lighter
        apiService.cache.set(cacheKey, {
          data: response.data,
          expiry: Date.now() + 2 * 60 * 1000,
        })
        return response.data
      } finally {
        apiService.loadingPromises.delete(cacheKey)
      }
    })
    apiService.loadingPromises.set(cacheKey, promise)
    return promise
  },
  
  // Récupération des options de filtres avec lazy loading optimisé et filtres contextuels
  async getFilterOptions(filterType, query = '', currentFilters = {}) {
    const cacheKey = `filter_${filterType}_${query}_${JSON.stringify(currentFilters)}`
    
    // Vérifier le cache d'abord
    if (apiService.isValidCache(cacheKey)) {
      return apiService.getCache(cacheKey)
    }

    // Vérifier si un appel est déjà en cours pour éviter les doublons
    if (apiService.loadingPromises.has(cacheKey)) {
      return apiService.loadingPromises.get(cacheKey)
    }

    const loadingPromise = apiService.withRetry(async () => {
      try {
        // Paramètres optimisés pour lazy loading
        const params = {
          limit: query ? 50 : 100, // Plus d'options si pas de recherche
        }
        if (query) {
          params.q = query
        }
        
        // Envoyer l'état complet des filtres actuels (toujours envoyer, même si vide)
        console.log(`[API] Sending filters for ${filterType}:`, currentFilters)
        params.filters = JSON.stringify(currentFilters || {})

        const response = await api.get(`/api/filters/${filterType}`, { params })
        
        // Validation des données
        if (!response.data) {
          return { options: [] }
        }
        
        // Si response.data est déjà un tableau, le wrapper dans options
        if (Array.isArray(response.data)) {
          // Valider et nettoyer les options
          const cleanOptions = response.data.filter(option => 
            option && 
            typeof option === 'object' && 
            option.value !== null && 
            option.value !== undefined &&
            typeof option.value === 'string' &&
            option.value.trim() !== ''
          )
          const result = { options: cleanOptions }
          apiService.setCache(cacheKey, result)
          return result
        }
        
        // Si response.data.options n'est pas un tableau, retourner un tableau vide
        if (!Array.isArray(response.data.options)) {
          const result = { options: [] }
          apiService.setCache(cacheKey, result)
          return result
        }
        
        // Valider et nettoyer les options
        const cleanOptions = response.data.options.filter(option => 
          option && 
          typeof option === 'object' && 
          option.value !== null && 
          option.value !== undefined &&
          typeof option.value === 'string' &&
          option.value.trim() !== ''
        )
        
        response.data.options = cleanOptions
        
        // Mettre en cache avec TTL plus long pour les recherches vides
        const result = response.data
        if (!query) {
          // Cache plus long pour les listes complètes
          const longCacheKey = `${cacheKey}_long`
          apiService.cache.set(longCacheKey, {
            data: result,
            expiry: Date.now() + (15 * 60 * 1000) // 15 minutes
          })
        }
        
        apiService.setCache(cacheKey, result)
        return result
      } finally {
        // Nettoyer la promesse en cours
        apiService.loadingPromises.delete(cacheKey)
      }
    })

    // Stocker la promesse pour éviter les appels dupliqués
    apiService.loadingPromises.set(cacheKey, loadingPromise)
    
    return loadingPromise
  },
  
  // Récupération des produits
  async getProduits(query = '', currentFilters = {}) {
    return this.getFilterOptions('produits', query, currentFilters)
  },
  
  // Récupération des importateurs
  async getImportateurs(query = '', currentFilters = {}) {
    return this.getFilterOptions('importateurs', query, currentFilters)
  },
  
  // Récupération des fournisseurs
  async getFournisseurs(query = '', currentFilters = {}) {
    return this.getFilterOptions('fournisseurs', query, currentFilters)
  },
  
  // Récupération des pays
  async getPays(query = '', currentFilters = {}) {
    return this.getFilterOptions('pays', query, currentFilters)
  },
  
  // Health check
  async healthCheck() {
    const response = await api.get('/api/health')
    return response.data
  },
  
  // Export des données — calls backend /api/export which fetches ALL matching rows
  async exportData(filters, format = 'csv', sortField = null, sortDirection = 'asc') {
    const response = await api.post('/api/export', {
      filters,
      export_format: format,
      table_sort: sortField,
      table_direction: sortDirection,
      include_kpi: false,
      include_timeseries: false,
      include_top: false,
      include_table: false,
    }, {
      responseType: 'blob',
      timeout: 120000, // 2 min — large exports can take time
    })

    // Extract filename from Content-Disposition header, fallback to generated name
    const cd = response.headers['content-disposition'] || ''
    let filename = `tradescope_export.${format === 'excel' ? 'xlsx' : 'csv'}`
    const match = cd.match(/filename="?([^"]+)"?/)
    if (match) filename = match[1]

    // Create download link
    const mimeMap = {
      excel: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      pdf: 'application/pdf',
      csv: 'text/csv;charset=utf-8;',
    }
    const mimeType = mimeMap[format] || mimeMap.csv
    const ext = format === 'excel' ? 'xlsx' : format
    if (!filename.endsWith(`.${ext}`)) {
      filename = `tradescope_export.${ext}`
    }
    const url = window.URL.createObjectURL(new Blob([response.data], { type: mimeType }))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  }
}

// Utilitaires pour les requêtes avec cache
class ApiCache {
  constructor(ttl = 5 * 60 * 1000) { // 5 minutes par défaut
    this.cache = new Map()
    this.ttl = ttl
  }
  
  get(key) {
    const item = this.cache.get(key)
    if (!item) return null
    
    if (Date.now() > item.expiry) {
      this.cache.delete(key)
      return null
    }
    
    return item.data
  }
  
  set(key, data) {
    this.cache.set(key, {
      data,
      expiry: Date.now() + this.ttl
    })
  }
  
  clear() {
    this.cache.clear()
  }
}

// Cache pour les options de filtres
const filterCache = new ApiCache(10 * 60 * 1000) // 10 minutes

// Service avec cache pour les filtres
export const cachedApiService = {
  ...apiServiceMethods,
  
  async getFilterOptions(filterType, query = '', currentFilters = {}) {
    const cacheKey = `${filterType}_${query}_${JSON.stringify(currentFilters)}`
    const cached = filterCache.get(cacheKey)
    
    if (cached) {
      return cached
    }
    
    const data = await apiServiceMethods.getFilterOptions(filterType, query, currentFilters)
    filterCache.set(cacheKey, data)
    
    return data
  }
}

export default api
